import { Flex } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { useClients } from 'src/clients/hooks/useClients';
import { ClientList, ClientListItem, FamilyList } from 'src/UI/generate-flow/ClientList';
import { filterAndOrderFamilies } from 'src/UI/generate-flow/familyList';
import { ClientFamilyToggle } from 'src/UI/generate-flow/ClientFamilyToggle';
import { useFamilies } from 'src/clients/hooks/useFamilies';
import { Family } from 'src/clients/hooks/useFamily';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { MeetingNoteStepProps } from '../types';

type Props = MeetingNoteStepProps & {
  preselectClientId?: string;
  preselectFamilyId?: string;
};

export function NoteClientStep({
  data,
  setData,
  filter,
  searchBar,
  preselectClientId,
  preselectFamilyId,
}: Props) {
  const { clients } = useClients();
  const { families } = useFamilies();
  const { flagEnabledClientSearchFullName } = useFlags();
  const term = (filter ?? '').trim().toLowerCase();

  const clientItems: ClientListItem[] = useMemo(() => {
    const base: ClientListItem[] = clients.map(c => ({ client: c }));

    base.sort((a, b) => {
      if (preselectClientId) {
        if (a.client.id === preselectClientId) return -1;
        if (b.client.id === preselectClientId) return 1;
      }
      return a.client.lastName.localeCompare(b.client.lastName);
    });

    if (!term) return base;
    return base.filter(({ client }) =>
      matchesClientSearch(
        [client.firstName, client.lastName, client.emailAddress],
        term,
        flagEnabledClientSearchFullName,
      ),
    );
  }, [clients, term, preselectClientId, flagEnabledClientSearchFullName]);

  const filteredFamilies = useMemo(
    () => filterAndOrderFamilies(families, term, preselectFamilyId),
    [families, term, preselectFamilyId],
  );

  const currentClientId = data.isFamily ? undefined : data.client?.id;
  const currentFamilyId = data.isFamily ? data.family?.id : undefined;

  useEffect(() => {
    if (preselectFamilyId) return;
    if (data.isFamily) return;
    if (currentClientId) return;
    if (!preselectClientId) return;

    const match = clients.find(c => c.id === preselectClientId);
    if (!match) return;

    setData(prev => {
      if (prev.isFamily) return prev;
      return { ...prev, client: match };
    });
  }, [clients, data.isFamily, currentClientId, preselectClientId, preselectFamilyId, setData]);

  useEffect(() => {
    if (!preselectFamilyId) return;
    if (currentFamilyId) return;

    const match = families.find(f => f.id === preselectFamilyId);
    if (!match) return;

    setData(prev => {
      if (prev.isFamily && prev.family?.id) return prev;
      return { ...prev, isFamily: true, client: undefined, family: match };
    });
  }, [families, currentFamilyId, preselectFamilyId, setData]);

  const title = data.isFamily
    ? 'Select the family to generate meeting note'
    : 'Select the client to generate meeting note';

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title={title} searchBar={searchBar}>
        <ClientFamilyToggle
          isFamily={data.isFamily}
          onToggle={isFamily => {
            setData(prev => {
              if (isFamily === prev.isFamily) return prev;
              return isFamily
                ? { ...prev, isFamily: true, family: undefined, client: undefined }
                : { ...prev, isFamily: false, client: undefined, family: undefined };
            });
          }}
        />
        {data.isFamily ? (
          <FamilyList
            items={filteredFamilies}
            selectedFamilyId={data.family?.id}
            onSelect={(family: Family) => {
              setData(prev => {
                if (!prev.isFamily) return prev;
                return { ...prev, family };
              });
            }}
          />
        ) : (
          <ClientList
            items={clientItems}
            selectedClientId={data.client?.id}
            onSelect={client => {
              setData(prev => {
                if (prev.isFamily) return prev;
                return { ...prev, client };
              });
            }}
          />
        )}
      </StepCard>
    </Flex>
  );
}
